
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
    reactive,
    onBeforeMount,
  } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { Field } from 'vee-validate';
  import LookupNameIds from '@/store/enums/settingsEnums';
  import LessonsFilter from '@/components/dropdown/LessonsFilter.vue';
  import ReportAttendence from '@/components/Reports/Attendence.vue';
  import { getUser } from '@/core/services/AuthService';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import { convertDateDDMMYYYY } from '@/utils/dateFormat';
  import moment from 'moment';
  import SwapLessons from '@/components/dropdown/SwapLessons.vue';
  import SearchField from '@/components/common/SearchField.vue';

  export default defineComponent({
    name: 'lessons-listing',
    components: {
      Datatable,
      Field,
      ReportAttendence,
      LessonsFilter,
      SwapLessons,
      SearchField,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const user = getUser();
      const isTrainer = user?.userType === 'trainer';

      const loading = ref(false);
      const showModal = ref(false);
      const showReasonModal = ref(false);
      const showMenu = ref(false);

      const horsesList = ref({});
      const classLevelList = ref();

      const searchQuery = ref({
        startDate: new Date()
          .toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
          .replace(/\//g, '-'),
        endDate: new Date()
          .toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
          .replace(/\//g, '-'),
        lessonId: route.query.lessonId,
      });

      const checkAttendenceResult = ref();

      const freezeDate = ref();
      const showFreezeDate = ref(false);
      const showCancellByKRC = ref(false);
      const cancellationReasonId = ref(0);
      const KRCCancelDate = ref();
      const showCancellationReason = ref(false);
      const showCommentModal = ref();

      const lessonComment = ref('');
      const lessonId = ref('');

      const dataReport = ref();

      const selectedRecored = ref<Array<string>>([]);
      const lessons = ref(
        [] as { lessonId: string; [key: string]: string | null | number }[]
      );

      let lookupsList;
      const disabledFilterButton = ref(true);
      const categoryList = ref();

      const tableMinHeight = () => {
        if (store.getters.lessonsList.length === 1) {
          const el = document.querySelector('.table-responsive');
          if (el) {
            (el as HTMLElement).style.minHeight = '290px';
          }
        }
      };

      onBeforeMount(async () => {
        if (route.query.lessonId) {
          searchQuery.value.lessonId = route.query.lessonId;
          searchQuery.value.startDate = '';
          searchQuery.value.endDate = '';
        }

        await store.dispatch(Actions.UPDATE_LESSONS_FILTER, searchQuery.value);
      });

      onMounted(async () => {
        loading.value = true;
        setCurrentPageBreadcrumbs(translate('lessonListing'), [
          translate('operations'),
        ]);
        await store.dispatch(Actions.GET_LESSONS_OPERATIONS, searchQuery.value);
        tableMinHeight();
        await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
          LookupNameIds.ATTENDEND,
          LookupNameIds.STATUS,
          LookupNameIds.HORSE_CATEGORY,
          LookupNameIds.CLASS_TYPE,
          LookupNameIds.CANCEL_REASON,
          LookupNameIds.DURATION,
          LookupNameIds.DAYS,
        ]);
        await store.dispatch(Actions.GET_ALL_ARENAS, { active: true });
        // await store.dispatch(Actions.GET_ALL_CUSTOMERS);
        await store.dispatch(Actions.GET_ALL_HORSES_OPERATIONS);
        await store.dispatch(Actions.GET_ALL_GROOMERS_OPERATIONS);
        categoryList.value = await store.dispatch(
          Actions.GET_ALL_SETTINGS_CLASS_CATEGORY
        );

        classLevelList.value = await store.dispatch(
          Actions.GET_ALL_SETTINGS_CLASS_LEVEL
        );

        if (!isTrainer) {
          await store.dispatch(Actions.GET_ALL_TRAINERS, {});
          dataReport.value = await store.dispatch(
            Actions.GET_REPORT_CUSTOMER_ATTENDENCE
          );
        }

        lookupsList = computed(() => store.getters.allLookupsList);

        loading.value = false;
        disabledFilterButton.value = false;
        reinitializeComponents();
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_LESSON_STORE);
      });
      const disabledField = ref([{}]);
      const disabledFieldOrStartTime = (id) => {
        const lesson = lessons.value.find((lesson) => lesson.lessonId === id);
        if (lesson && lesson.startDate) {
          disabledField.value[id] = false;
          return true;
        } else {
          disabledField.value[id] = true;
          return false;
        }
      };
      function getEffectiveTime(lesson: { id: string; time?: string }) {
        const pending = lessons.value.find((l) => l.lessonId === lesson.id);
        return pending?.time ?? lesson.time ?? '';
      }
      const handleTimeChange = (
        lesson: { id: string; horseId?: string },
        event: Event
      ) => {
        const target = event?.target as HTMLInputElement | null;
        const newTime = target?.value ?? '';
        if (!newTime) return;
        const horseId = lesson.horseId ?? '';
        handleSelectedField(lesson.id, horseId, 'horseId', newTime);
      };

      const handleSelectedField = (
        lessonId: string,
        fieldId: string,
        fieldName: string,
        time?: string
      ) => {
        const lessonIndex = lessons.value.findIndex(
          (lesson) => lesson.lessonId === lessonId
        );

        if (lessonIndex !== -1 && time) {
          lessons.value[lessonIndex].time = time;
        }

        let newLesson;
        if (lessonIndex === -1) {
          if (time) {
            newLesson = {
              lessonId: lessonId,
              [fieldName]: fieldId,
              time,
            };
          } else {
            newLesson = {
              lessonId: lessonId,
              [fieldName]: fieldId,
            };
          }

          lessons.value.push(newLesson);
        } else {
          lessons.value[lessonIndex][fieldName] = fieldId;
        }
        disabledFieldOrStartTime(lessonId);
      };

      const handleCategoryChanged = (lesson, categoryId) => {
        const categoryObj = categoryList.value.find((category) => {
          return category.id === categoryId;
        });
        matchedClassLevelList.value[lesson.id] = classLevelList.value.filter(
          (classLevel) => categoryObj.classLevelId?.includes(classLevel.id)
        );
        const lessonIndex = lessons.value.findIndex(
          (l) => l.lessonId === lesson.id
        );
        lessons.value[lessonIndex].classLevelId = null;
      };

      const isRecordSelected = (id: string) => {
        return selectedRecored.value.includes(id);
      };

      const selectableRowsOnCurrentPage = computed(() =>
        store.getters.lessonsList.filter(
          (row: { attendanceName: string }) =>
            !isSelectRecordEnabled(row.attendanceName)
        )
      );

      const allSelectableSelected = computed(() => {
        const selectable = selectableRowsOnCurrentPage.value;
        return (
          selectable.length > 0 &&
          selectable.every((r: { id: string }) =>
            selectedRecored.value.includes(r.id)
          )
        );
      });

      const indeterminate = computed(() => {
        const selectable = selectableRowsOnCurrentPage.value;
        const someSelected = selectable.some((r: { id: string }) =>
          selectedRecored.value.includes(r.id)
        );
        return someSelected && !allSelectableSelected.value;
      });

      const toggleSelectAll = () => {
        const selectable = selectableRowsOnCurrentPage.value;
        if (allSelectableSelected.value) {
          const toRemoveIds = selectable.map((r: { id: string }) => r.id);
          selectedRecored.value = selectedRecored.value.filter(
            (id) => !toRemoveIds.includes(id)
          );
          toRemoveIds.forEach((id) => {
            lessons.value = lessons.value.filter((l) => l.lessonId !== id);
            const idx = checkLessosns.value.findIndex(
              (l: { lessonId: string }) => l.lessonId === id
            );
            if (idx !== -1) checkLessosns.value.splice(idx, 1);
            const swapIdx = swappedLessons.value.findIndex(
              (s: { id: string }) => s.id === id
            );
            if (swapIdx !== -1) swappedLessons.value.splice(swapIdx, 1);
            delete horsesList.value[id];
          });
        } else {
          bulkArenaId.value = '';
          bulkTrainerId.value = '';
          bulkTime.value = '';
          selectable.forEach((lesson: { id: string }) => {
            if (!selectedRecored.value.includes(lesson.id)) {
              selectedRecored.value.push(lesson.id);
            }
          });
        }
      };

      const onRowCheckboxChange = (
        lesson: Record<string, unknown> & { id: string }
      ) => {
        prepareLessonToCheck(lesson);
      };

      const selectedLessonsForBulk = computed(() =>
        store.getters.lessonsList.filter((l: { id: string }) =>
          selectedRecored.value.includes(l.id)
        )
      );

      const bulkArenaId = ref<string | number>('');
      const bulkTrainerId = ref<string | number>('');
      const bulkTime = ref('');

      const applyBulkArena = (arenaId: string | number) => {
        bulkArenaId.value = arenaId;
        const idStr = String(arenaId);
        selectedLessonsForBulk.value.forEach((lesson: { id: string }) => {
          handleSelectedField(lesson.id, idStr, 'arenaId');
        });
      };

      const applyBulkTrainer = (trainerId: string | number) => {
        bulkTrainerId.value = trainerId;
        const idStr = String(trainerId);
        selectedLessonsForBulk.value.forEach((lesson: { id: string }) => {
          handleSelectedField(lesson.id, idStr, 'trainerId');
        });
      };

      const applyBulkTime = () => {
        const newTime = bulkTime.value;
        if (!newTime) return;
        selectedLessonsForBulk.value.forEach(
          (lesson: { id: string; horseId?: string }) => {
            const horseId = lesson.horseId ?? '';
            handleSelectedField(lesson.id, horseId, 'horseId', newTime);
          }
        );
      };

      const enableEditing = (id: string, lessonAttendence: string) => {
        if (!isTrainer) {
          if (lessonAttendence === 'pending' && isRecordSelected(id)) {
            return true;
          }
        }
        return false;
      };

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const excelHeader = reactive({
        Horse: 'horseName',
        Trainer: 'trainerName',
        Customer: 'customerName',
        Groomer: 'groomerName',
        Arena: 'arenaName',
        Duration: 'durationName',
        Status: 'attendance',
        Balance: 'price',
        Phone: 'phone',
        Time: 'time.startTime',
        'Start Date': 'startDate',
        Category: 'classCategoryName',
        'Class Level': 'classLevelName',
        'Lesson Type': 'lessonType',
      });
      const tableHeader = ref([
        {
          key: 'checkbox',
          sortable: false,
          cellWidth: '30px',
        },
        {
          name: translate('start Date'),
          key: 'startDate',
          sortable: true,
        },
        {
          name: translate('time'),
          key: 'time',
          sortable: true,
          cellWidth: '100px',
        },
        {
          name: translate('trainer Name'),
          key: 'trainerName',
          sortable: true,
          cellWidth: '125px',
        },
        {
          name: translate('customer Name'),
          key: 'customerName',
          sortable: true,
          cellWidth: '125px',
        },
        {
          name: translate('balance'),
          key: 'balance',
          sortable: true,
          cellWidth: '80px',
        },
        {
          name: translate('horse Name'),
          key: 'horseName',
          sortable: true,
          cellWidth: '125px',
        },
        {
          name: translate('arena Name'),
          key: 'arenaName',
          sortable: true,
        },

        {
          name: translate('groomer'),
          key: 'groomerName',
          sortable: true,
          cellWidth: '125px',
        },
        {
          name: translate('day'),
          key: 'dayName',
          sortable: true,
          cellWidth: '100px',
        },
        {
          name: translate('duration'),
          key: 'durationName',
          sortable: true,
          cellWidth: '80px',
        },
        {
          name: translate('category'),
          key: 'classCategory',
          sortable: true,
          cellWidth: '125px',
        },
        {
          name: translate('classLevel'),
          key: 'classLevelName',
          sortable: true,
        },
        {
          name: translate('type'),
          key: 'classType',
          sortable: true,
        },
        {
          name: translate('horse Location'),
          key: 'horseLocation',
          sortable: true,
        },
        {
          name: translate('comment'),
          key: 'comment',
          sortable: true,
        },
        {
          name: translate('Price'),
          key: 'price',
          sortable: true,
          cellWidth: '120px',
        },
        {
          name: translate('status'),
          key: 'attendanceName',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const toggleStatusUser = async (lesson) => {
        const result = await Swal.fire({
          title: translate('You are about to delete this lesson'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_LESSONS, lesson.id);
          await store.dispatch(
            Actions.GET_LESSONS_OPERATIONS,
            searchQuery.value
          );
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          }
        }
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_LESSONS_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_LESSONS_OPERATIONS, searchQuery.value);
        tableMinHeight();
        loading.value = false;
      };

      const updateLessonsInformation = async () => {
        lessons.value = lessons.value.map((lesson) => {
          if (lesson.startDate) {
            return {
              startDate: moment(lesson.startDate).format('YYYY-MM-DD'),
              lessonId: lesson.lessonId,
            };
          } else {
            return lesson;
          }
        });
        const checkClassLevelId = lessons.value.filter(
          (lesson) => lesson.classCategoryId && !lesson.classLevelId
        );
        if (checkClassLevelId.length > 0) {
          Swal.fire({
            text: translate('please select class level'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          return;
        }

        const data = await store.dispatch(Actions.UPDATE_LESSONS, {
          data: { lessons: [...lessons.value] },
        });

        if (Array.isArray(data.data)) {
          Swal.fire({
            text: translate(data.data[0].reason),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          selectedRecored.value = [];
          swappedLessons.value = [];
          data.data.map((record) => {
            record.lesson.map((lesson) => {
              selectedRecored.value.push(lesson.id);
            });
          });

          await store.dispatch(
            Actions.GET_LESSONS_OPERATIONS,
            searchQuery.value
          );
          tableMinHeight();
          return;
        }
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } else {
          selectedRecored.value = [];
          swappedLessons.value = [];

          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_LESSONS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
          loading.value = true;
          await store.dispatch(
            Actions.GET_LESSONS_OPERATIONS,
            searchQuery.value
          );
          tableMinHeight();

          loading.value = false;
          selectedRecored.value = [];
          lessons.value = [];
          checkLessosns.value = [];
          swappedLessons.value = [];
          bulkArenaId.value = '';
          bulkTrainerId.value = '';
          bulkTime.value = '';
        }
      };

      interface ICheckLesson {
        lessonId: string;
        customerId: string;
        referenceId: string;
        dayId: string;
      }
      const checkLessosns = ref<Array<ICheckLesson>>([]);
      const swappedLessons = ref(
        [] as Array<{
          id: string;
          trainerId: string;
          horseId: string;
          groomerId: string;
          arenaId: string;
        }>
      );

      const recoverData = async (lessonId, isLastInGroup) => {
        const { data } = await store.dispatch(Actions.GET_LESSON, lessonId);
        const lessonIndex = store.getters.lessonsList.findIndex(
          (lesson) => lesson.id === lessonId
        );
        if (lessonIndex !== -1) {
          store.getters.lessonsList[lessonIndex] = data.data;
          store.getters.lessonsList[lessonIndex].isLastInGroup = isLastInGroup;
        }
      };

      const matchedClassLevelList = ref({});
      const prepareLessonToCheck = async (lessonObj) => {
        const horsePayload = {
          time: lessonObj.time,
          startDate: lessonObj.startDate,
          numberOfLessons: 1,
          durationId: lessonObj.durationId,
          customerId: lessonObj.customerId,
          days: [lessonObj.dayId],
        };

        const lessonIndex = checkLessosns.value.findIndex(
          (lesson: { lessonId: string }) => lesson.lessonId === lessonObj.id
        );
        if (lessonIndex === -1) {
          delete horsesList[lessonObj.id];
          delete matchedClassLevelList.value[lessonObj.id];

          const categoryObj = categoryList.value.find((category) => {
            return category.id === lessonObj.classCategoryId;
          });
          matchedClassLevelList.value[lessonObj.id] =
            classLevelList.value.filter((classLevel) =>
              categoryObj.classLevelId?.includes(classLevel.id)
            );
          let data = await store.dispatch(
            Actions.GET_ALL_LESSONS_HORSE,
            horsePayload
          );
          data = [
            {
              nameEn: lessonObj.horseName,
              nameAr: lessonObj.horseName,
              id: lessonObj.horseId,
            },
            ...data,
          ];

          horsesList.value[lessonObj.id] = data;
          const newLesson: ICheckLesson = {
            lessonId: lessonObj.id,
            customerId: lessonObj.customerId,
            referenceId: lessonObj.referenceId,
            dayId: lessonObj.dayId,
          };

          checkLessosns.value.push(newLesson);
          if (swappedLessons.value.length < 2) {
            swappedLessons.value.push({
              id: lessonObj.id,
              trainerId: lessonObj.trainerId,
              horseId: lessonObj.horseId,
              groomerId: lessonObj.groomerId,
              arenaId: lessonObj.arenaId,
            });
          }
        } else {
          checkLessosns.value.splice(lessonIndex, 1);
          swappedLessons.value.length > 2 &&
            swappedLessons.value.splice(lessonIndex, 1);
          lessons.value = lessons.value.filter(
            (lesson) => lesson.lessonId !== lessonObj.id
          );
          recoverData(lessonObj.id, lessonObj.isLastInGroup);
        }
      };

      const isSelectRecordEnabled = (lessonStatus) => {
        if (user?.isSuperAdmin) {
          return false;
        }
        if (lessonStatus === 'pending') {
          return false;
        } else {
          return true;
        }
      };
      const payload = ref();
      const disableCompleteButton = () => {
        if (isLastCheckingHasError.value) {
          return false;
        }
        const totalLengthLesson = checkAttendenceResult?.value?.reduce(
          (accumulator, obj) => {
            if (obj.lesson && Array.isArray(obj.lesson)) {
              return accumulator + obj.lesson.length;
            }
            return accumulator;
          },
          0
        );

        if (checkAttendenceResult?.value && checkLessosns.value.length > 0)
          if (totalLengthLesson === checkLessosns.value.length) {
            return false;
          }
        if (showFreezeDate.value) {
          if (!freezeDate.value) {
            return false;
          }
        }
        if (showCancellationReason.value) {
          if (!cancellationReasonId.value) {
            return false;
          }
        }
        if (showCancellByKRC.value) {
          if (!KRCCancelDate.value) {
            return false;
          }
        }

        return true;
      };
      let attendanceParam = ref();

      const syncCheckLessonsFromSelection = () => {
        const list = store.getters.lessonsList as Array<{
          id: string;
          customerId: string;
          referenceId: string;
          dayId: string;
        }>;
        selectedRecored.value.forEach((id) => {
          const already = checkLessosns.value.some((l) => l.lessonId === id);
          if (already) return;
          const row = list.find((l) => l.id === id);
          if (row) {
            checkLessosns.value.push({
              lessonId: row.id,
              customerId: row.customerId,
              referenceId: row.referenceId,
              dayId: row.dayId,
            });
          }
        });
      };

      const checkLessonsAttendence = async (
        attendence = attendanceParam.value,
        ignoreCancelCondition = false
      ) => {
        attendanceParam.value = attendence;
        showFreezeDate.value = false;
        showCancellationReason.value = false;
        showCancellByKRC.value = false;
        showReasonModal.value = false;

        const isFreez = attendence.slug === 'freeze';
        const isCancellation = attendence.slug === 'cancelled';
        const isCancellByKRC = attendence.slug === 'cancelled-by-krc';
        if (isFreez && !freezeDate.value) {
          showFreezeDate.value = true;
          showReasonModal.value = true;
          return;
        } else if (isCancellation && !cancellationReasonId.value) {
          showCancellationReason.value = true;
          showReasonModal.value = true;
          return;
        } else if (isCancellByKRC && !ignoreCancelCondition) {
          showCancellByKRC.value = true;
          showReasonModal.value = true;
          return;
        }
        syncCheckLessonsFromSelection();
        payload.value = {
          lessons: checkLessosns.value,
          attendance: attendence.id,
        };
        isFreez ? (payload.value.startDate = freezeDate.value) : '';
        isCancellByKRC ? (payload.value.startDate = KRCCancelDate) : '';

        isCancellation
          ? (payload.value.cancellationReasonId = cancellationReasonId.value)
          : '';

        const { data } = await store.dispatch(
          Actions.CHECK_ATTENDENCE,
          payload.value
        );

        if (Array.isArray(data.data)) {
          checkAttendenceResult.value = data.data;
          resetAttendence();
          showModal.value = true;

          return;
        } else {
          await completeUpdateAttendence();
        }
      };
      const isLastCheckingHasError = ref(false);
      const completeUpdateAttendence = async () => {
        const result = await Swal.fire({
          title: translate('Are you sure you want to update the attendance?'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          const { data } = await store.dispatch(
            Actions.COMPLETE_ATTENDENCE,
            payload.value
          );
          payload.value = {};
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            if (Array.isArray(data.data)) {
              checkAttendenceResult.value = data.data;
              showModal.value = true;
              isLastCheckingHasError.value = true;
              return;
            } else {
              Swal.fire({
                text: translate('SUCCESSFULLY_UPDATED_LESSONS'),
                icon: 'success',
                buttonsStyling: false,
                confirmButtonText: translate('ok'),
                customClass: {
                  confirmButton: 'btn btn-light-primary',
                },
              });
              loading.value = true;
              await store.dispatch(
                Actions.GET_LESSONS_OPERATIONS,
                searchQuery.value
              );
              tableMinHeight();

              loading.value = false;
              showModal.value = false;
              selectedRecored.value = [];
              checkLessosns.value = [];
              resetAttendence();
            }
          }
        }
      };
      const resetAttendence = () => {
        freezeDate.value = undefined;
        cancellationReasonId.value = 0;
        showModal.value = false;
        showReasonModal.value = false;
        showCancellByKRC.value = false;
        KRCCancelDate.value = undefined;
        isLastCheckingHasError.value = false;
      };

      const onReset = async () => {
        loading.value = true;
        selectedRecored.value = [];
        checkLessosns.value = [];
        lessons.value = [];
        await store.dispatch(Actions.UPDATE_LESSONS_FILTER, {
          startDate: new Date().toISOString().slice(0, 10),
          endDate: new Date().toISOString().slice(0, 10),
        });
        await store.dispatch(Actions.UPDATE_LESSONS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_LESSONS_OPERATIONS);
        selectedRecored.value = [];
        tableMinHeight();
        loading.value = false;
      };
      const searchLessons = async (query) => {
        searchQuery.value = query;
        loading.value = true;
        selectedRecored.value = [];
        checkLessosns.value = [];
        lessons.value = [];
        await store.dispatch(Actions.UPDATE_LESSONS_FILTER, query);
        await store.dispatch(Actions.UPDATE_LESSONS_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_LESSONS_OPERATIONS);
        tableMinHeight();
        loading.value = false;
      };
      const swapLesson = async (swapType) => {
        loading.value = true;
        const payload = {
          lessons: swappedLessons.value,
          swapType,
        };
        await store.dispatch(Actions.SWAP_LESSONS, payload);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_LESSONS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
          await store.dispatch(
            Actions.GET_LESSONS_OPERATIONS,
            searchQuery.value
          );
          tableMinHeight();
        }
        swappedLessons.value = [];
        selectedRecored.value = [];
        checkLessosns.value = [];
        loading.value = false;
      };

      const excelData = computed(() => {
        const flattenedLessons = dataReport.value?.data.reduce(
          (result, item) => {
            return result.concat(item.lessons);
          },
          []
        );

        return flattenedLessons?.map((lesson) => {
          return {
            ...lesson,
            startDate: lesson.startDate.slice(0, 10), // Slice the date string
          };
        });
      });

      const format12 = (time) => {
        return moment(time, 'HH:mm').format('hh:mm A');
      };

      return {
        tableData: computed(() => store.getters.lessonsList),
        arenas: computed(() => store.getters.allArenasList),
        horsesList,
        customers: computed(() => store.getters.allCustomersList),
        trainers: computed(() => store.getters.allTrainersList),
        groomers: computed(() => store.getters.allGroomerList),
        totalItems: computed(() => store.getters.lessonslistCount),
        dayList: computed(() => {
          return lookupsList.value.filter(
            (lookup) => lookup.lookupNameId === LookupNameIds.DAYS
          );
        }),
        attendncies: computed(() => {
          return lookupsList.value.filter(
            (lookup) => lookup.lookupNameId === LookupNameIds.ATTENDEND
          );
        }),
        cancelReason: computed(() => {
          return lookupsList.value.filter(
            (lookup) => lookup.lookupNameId === LookupNameIds.CANCEL_REASON
          );
        }),
        classTypeList: computed(() => {
          return lookupsList.value.filter(
            (lookup) => lookup.lookupNameId === LookupNameIds.CLASS_TYPE
          );
        }),
        durationList: computed(() => {
          return lookupsList.value.filter(
            (lookup) => lookup.lookupNameId === LookupNameIds.DURATION
          );
        }),

        excelData,
        tableHeader,
        toggleStatusUser,
        translate,
        pageChanged,
        goTo,
        loading,
        can,
        excelHeader,
        selectedRecored,
        isRecordSelected,
        allSelectableSelected,
        indeterminate,
        toggleSelectAll,
        onRowCheckboxChange,
        bulkArenaId,
        bulkTrainerId,
        bulkTime,
        applyBulkArena,
        applyBulkTrainer,
        applyBulkTime,
        getEffectiveTime,
        handleTimeChange,
        handleSelectedField,
        updateLessonsInformation,
        prepareLessonToCheck,
        checkLessonsAttendence,
        showModal,
        checkAttendenceResult,
        completeUpdateAttendence,
        showMenu,
        onReset,
        searchLessons,
        payload,
        freezeDate,
        showFreezeDate,
        showCancellByKRC,
        KRCCancelDate,
        cancellationReasonId,
        showCancellationReason,
        showCommentModal,
        disableCompleteButton,
        showReasonModal,
        attendanceParam,
        resetAttendence,
        lessonComment,
        lessonId,
        disabledFilterButton,
        lessons,
        isTrainer,
        enableEditing,
        convertDateDDMMYYYY,
        format12,
        swapLesson,
        disabledFieldOrStartTime,
        disabledField,
        categoryList,
        classLevelList,
        matchedClassLevelList,
        handleCategoryChanged,
        isSelectRecordEnabled,
      };
    },
  });
